/**
 * Sidebar.jsx (PR_RUN_035 / refined by PR_RUN_039)
 * =================================================
 * Sidebar navigation component for QMSLayout.
 *
 * PR_RUN_035 (initial extraction) — extracted JSX wholesale from
 *   QMSLayout.js as a pure presentational component with 18 props.
 *
 * PR_RUN_039 (state colocation) — moved all sidebar-internal state into
 *   this component (`expandedGroups`, `sidebarFilter`, `sidebarLangOpen`)
 *   + replaced injected `navigate`/`location`/`t`/`i18n` with hooks.
 *   Prop count shrank 18 → 9. Behavior preserved exactly.
 *
 * State ownership after PR_RUN_039:
 *   - QMSLayout owns: `sidebarOpen` (shared with topbar hamburger),
 *     `navGroups` / `adminItems` (also passed to CommandPalette),
 *     `user`, `handleLogout`, `currentLang`, `changeLang`,
 *     `supportedLanguages`.
 *   - Sidebar owns: filter input, expandedGroups + localStorage persist,
 *     toggleGroup, sidebar-language dropdown open/close.
 *
 * Code-review note from the test agent (closed PR_RUN_039).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronDown, ChevronRight, LogOut, Search, User, Languages, X,
} from "lucide-react";
import FavouritesBar from "./FavouritesBar";
import { APP_VERSION } from "../appVersion";
import { GuideReopenButton, GuideResetButton } from "./QuickStartGuide";

const EXPANDED_GROUPS_LS_KEY = "qms.sidebar.expandedGroups.v1";

const _hydrateExpandedGroups = () => {
  try {
    const raw = window.localStorage.getItem(EXPANDED_GROUPS_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const Sidebar = ({
  // Shared with topbar / mobile backdrop
  sidebarOpen,
  setSidebarOpen,
  // Nav data (already role-filtered upstream; passed to keep CommandPalette
  // and Sidebar in sync from a single derivation in QMSLayout).
  navGroups,
  adminItems,
  isAdmin,
  // User chrome
  user,
  handleLogout,
  // Language switcher
  currentLang,
  changeLang,
  supportedLanguages,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(["nav", "common"]);

  // ── Sidebar-internal state (PR_RUN_039) ──────────────────────────────
  const [expandedGroups, setExpandedGroups] = useState(_hydrateExpandedGroups);
  const [sidebarFilter, setSidebarFilter] = useState("");
  const [sidebarLangOpen, setSidebarLangOpen] = useState(false);

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((g) => g !== groupId) : [...prev, groupId]
    );
  }, []);

  // PR_RUN_031 — persist sidebar expansion state across reloads.
  useEffect(() => {
    try {
      window.localStorage.setItem(EXPANDED_GROUPS_LS_KEY, JSON.stringify(expandedGroups));
    } catch (_) {
      /* localStorage unavailable (private browsing) — silently degrade */
    }
  }, [expandedGroups]);

  // PR_RUN_031 — sidebar quick-filter. Filters items by label substring
  // (case-insensitive). Non-empty filter auto-expands every matching group.
  const filterTerm = sidebarFilter.trim().toLowerCase();
  const filteredNavGroups = useMemo(() => (
    filterTerm
      ? (navGroups || [])
          .map((group) => ({
            ...group,
            items: (group.items || []).filter((item) =>
              (item.label || "").toLowerCase().includes(filterTerm)
            ),
          }))
          .filter((group) => group.items.length > 0)
      : (navGroups || [])
  ), [filterTerm, navGroups]);

  const effectiveExpandedGroups = filterTerm
    ? [...filteredNavGroups.map((g) => g.id), "admin"]
    : expandedGroups;

  const filteredAdminItems = useMemo(() => (
    filterTerm
      ? (adminItems || []).filter((item) =>
          (item.label || "").toLowerCase().includes(filterTerm)
        )
      : (adminItems || [])
  ), [filterTerm, adminItems]);

  return (
    <aside className={`qms-sidebar ${sidebarOpen ? "open" : ""}`} data-testid="qms-sidebar">
      {/* Logo */}
      <div className="p-5 border-b border-[rgba(255,255,255,0.06)]">
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="Charstan" className="h-10" style={{ filter: "brightness(1.1)" }} />
        </div>
      </div>

      {/* Favourites/Shortcuts */}
      <FavouritesBar />

      {/* PR_RUN_031 — sidebar quick filter */}
      <div className="px-3 pt-2 pb-1">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={sidebarFilter}
            onChange={(e) => setSidebarFilter(e.target.value)}
            placeholder="Filter menu  ·  ⌘K for jump"
            className="w-full pl-7 pr-7 py-1.5 bg-[#0F1E35] border border-[#1E3A5F] rounded text-[12px] text-slate-200 placeholder-slate-500 focus:outline-none focus:border-[#00C8E0] transition-colors"
            data-testid="sidebar-filter-input"
          />
          {sidebarFilter && (
            <button
              type="button"
              onClick={() => setSidebarFilter("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              aria-label="clear filter"
              data-testid="sidebar-filter-clear"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto sidebar-nav">
        {filteredNavGroups.length === 0 && (
          <div className="px-5 py-4 text-xs text-slate-500" data-testid="sidebar-filter-empty">
            No menu items match &ldquo;{sidebarFilter}&rdquo;.
          </div>
        )}
        {filteredNavGroups.map((group) => (
          <div key={group.id} className="mb-1">
            <button
              className="w-full flex items-center justify-between px-5 py-2 text-[#8B95A5] text-[11px] uppercase tracking-widest font-medium hover:text-[#CBD5E1] transition-colors"
              onClick={() => toggleGroup(group.id)}
              data-testid={`nav-group-${group.id}`}
            >
              {group.label}
              {effectiveExpandedGroups.includes(group.id) ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
            </button>
            {effectiveExpandedGroups.includes(group.id) && (
              <div className="mt-0.5 mb-2">
                {group.items.map((item) => (
                  <div
                    key={item.path}
                    className={`sidebar-item ${location.pathname === item.path ? "active" : ""} ${item.highlight ? "ai-highlight" : ""}`}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon size={16} />
                    <span>{item.label}</span>
                    {item.highlight && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#E8A838] animate-pulse" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Admin Section */}
        {isAdmin && filteredAdminItems.length > 0 && (
          <div className="mb-1 mt-2 pt-2 border-t border-[rgba(255,255,255,0.06)]">
            <button
              className="w-full flex items-center justify-between px-5 py-2 text-[#8B95A5] text-[11px] uppercase tracking-widest font-medium hover:text-[#CBD5E1] transition-colors"
              onClick={() => toggleGroup("admin")}
              data-testid="nav-group-admin"
            >
              {t("nav:groups.admin")}
              {effectiveExpandedGroups.includes("admin") ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
            </button>
            {effectiveExpandedGroups.includes("admin") && (
              <div className="mt-0.5 mb-2">
                {filteredAdminItems.map((item) => (
                  <div
                    key={item.path}
                    className={`sidebar-item ${location.pathname === item.path ? "active" : ""}`}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon size={16} />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-[rgba(255,255,255,0.06)]">
        {/* Getting Started Guide */}
        <div className="mb-2">
          <GuideReopenButton onClick={() => {
            const guideRole = user?.role || "client";
            navigate(`/guide/${guideRole}`);
            setSidebarOpen(false);
          }} />
          <GuideResetButton onReset={() => {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user-guide/reset`, {
              method: "POST", credentials: "include",
            }).then(() => {
              navigate("/dashboard");
              setSidebarOpen(false);
              window.location.reload();
            });
          }} />
        </div>
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#2D313D] flex items-center justify-center">
              <User size={14} className="text-[#8B95A5]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[#8B95A5] text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 text-[#F87171] bg-[rgba(248,113,113,0.08)] hover:bg-[rgba(248,113,113,0.15)] rounded-[3px] transition-colors text-sm"
          data-testid="sidebar-logout-btn"
        >
          <LogOut size={14} />
          <span>{t("nav:logout")}</span>
        </button>
        {/* Language Switcher - Sidebar */}
        <div className="relative mt-3">
          <button
            onClick={() => setSidebarLangOpen(!sidebarLangOpen)}
            className="w-full flex items-center justify-center gap-2 py-2 px-3 text-[#8B95A5] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] rounded-[3px] transition-colors text-sm"
            data-testid="language-switcher-btn"
          >
            <Languages size={14} />
            <span>{currentLang.flag} {currentLang.shortName}</span>
            <ChevronDown size={12} className={`transition-transform ${sidebarLangOpen ? "rotate-180" : ""}`} />
          </button>
          {sidebarLangOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] shadow-lg z-50" data-testid="language-dropdown">
              {supportedLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { changeLang(lang.code); setSidebarLangOpen(false); }}
                  className={`w-full px-3 py-2 flex items-center gap-2 text-sm transition-colors ${
                    i18n.language === lang.code
                      ? "bg-[#36A9E1] bg-opacity-15 text-[#38BDF8]"
                      : "text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.04)]"
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                  {i18n.language === lang.code && (
                    <span className="ml-auto text-[#38BDF8]">&#10003;</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* PR_RUN_056 — Deployment / fork-session version stamp. Footer
            badge at the very bottom of the sidebar so it's quietly
            visible across the entire authenticated app surface. */}
        <div
          className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.04)] text-center text-[10px] text-[#555] font-mono tracking-tight select-text"
          data-testid="sidebar-app-version"
          title="App deployment / fork-session version stamp"
        >
          {APP_VERSION}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
